import React, { useEffect, useState, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import CarLoaderFull from '../PageLoader/CarLoaderFull/CarLoaderFull';
import DefaultLoader from '../PageLoader/DefaultLoader/DefaultLoader';

// Global Network Tracker
let activeRequests = 0;
let subscribers = [];

const notify = () => {
  subscribers.forEach((callback) => callback(activeRequests));
};

if (typeof window !== 'undefined') {
  // Monkey-patch fetch to track active network requests
  const originalFetch = window.fetch;
  window.fetch = async (...args) => {
    activeRequests++;
    notify();
    try {
      const response = await originalFetch(...args);
      return response;
    } finally {
      activeRequests--;
      notify();
    }
  };

  // Monkey-patch XMLHttpRequest to track active network requests
  const originalXhrOpen = window.XMLHttpRequest.prototype.open;
  const originalXhrSend = window.XMLHttpRequest.prototype.send;
  
  window.XMLHttpRequest.prototype.open = function (...args) {
    this._isTracked = false;
    return originalXhrOpen.apply(this, args);
  };

  window.XMLHttpRequest.prototype.send = function (...args) {
    if (!this._isTracked) {
      this._isTracked = true;
      activeRequests++;
      notify();
      
      const onEnd = () => {
        activeRequests--;
        notify();
        this.removeEventListener('loadend', onEnd);
      };
      this.addEventListener('loadend', onEnd);
    }
    return originalXhrSend.apply(this, args);
  };
}

const GlobalTransitionLoader = () => {
  const location = useLocation();
  const [isLoading, setIsLoading] = useState(false);
  const isInitialMount = useRef(true);
  const minTimeoutRef = useRef(null);
  const maxTimeoutRef = useRef(null);
  const minTimeElapsed = useRef(false);

  const hideLoader = () => {
    setIsLoading(false);
    document.body.style.pointerEvents = 'auto'; // Re-enable clicks
  };

  useEffect(() => {
    const handleLangChangeStart = () => {
      if (window.location.pathname.includes('domestic') || window.location.pathname.includes('travel')) return;
      setIsLoading(true);
      window.scrollTo({ top: 0, behavior: 'smooth' });
      document.body.style.pointerEvents = 'none';
    };
    
    if (typeof window !== 'undefined') {
      window.addEventListener('languageChangeStart', handleLangChangeStart);
      return () => window.removeEventListener('languageChangeStart', handleLangChangeStart);
    }
  }, []);

  useEffect(() => {
    if (location.pathname.includes('domestic') || location.pathname.includes('travel')) return;

    let wasLangSwitch = false;
    if (typeof window !== 'undefined') {
      try {
        if (sessionStorage.getItem('langSwitching') === 'true') {
          wasLangSwitch = true;
          sessionStorage.removeItem('langSwitching');
        }
      } catch (e) {}
    }

    // Avoid showing loader on initial SSR load
    if (isInitialMount.current && !wasLangSwitch) {
      isInitialMount.current = false;
      return;
    }
    isInitialMount.current = false;
    
    // Start navigation loader
    setIsLoading(true);
    minTimeElapsed.current = false;
    
    // Scroll to top smoothly
    window.scrollTo({ top: 0, behavior: 'smooth' });
    
    // Disable multiple clicks globally
    document.body.style.pointerEvents = 'none';
    
    // Clear existing timeouts
    if (minTimeoutRef.current) clearTimeout(minTimeoutRef.current);
    if (maxTimeoutRef.current) clearTimeout(maxTimeoutRef.current);

    // Minimum display time for visual smoothness and to allow new components to mount & fire APIs
    minTimeoutRef.current = setTimeout(() => {
      minTimeElapsed.current = true;
      if (activeRequests === 0) {
        hideLoader();
      }
    }, 600);
    
    // Maximum display time as a safety net (5 seconds)
    maxTimeoutRef.current = setTimeout(() => {
      hideLoader();
    }, 5000);

  }, [location.pathname]);

  useEffect(() => {
    const handleNetworkChange = (count) => {
      // If we are currently loading, min time has passed, and no active requests remain
      if (isLoading && minTimeElapsed.current && count === 0) {
        hideLoader();
      }
    };

    subscribers.push(handleNetworkChange);
    return () => {
      subscribers = subscribers.filter(cb => cb !== handleNetworkChange);
    };
  }, [isLoading]);

  // Clean up pointer events on unmount just in case
  useEffect(() => {
    return () => {
      document.body.style.pointerEvents = 'auto';
    };
  }, []);

  if (!isLoading) return null;

  const motorJourneyRoutes = [
    'motor-basic-form',
    'motor-vehicle-info',
    'motor-valuation',
    'motor-licence-info',
    'motor-best-deals',
    'motor-addons',
    'motor-add-vehicle-owners-name',
    'motor-summary',
    'motor-upload',
    'motor-live-documents',
    'motor-payment'
  ];

  const isMotorJourney = motorJourneyRoutes.some(route => location.pathname.includes(route));
  
  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      width: '100%',
      height: '100vh',
      zIndex: 99999,
      backgroundColor: 'rgba(255,255,255,0.9)',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center'
    }}>
      {isMotorJourney ? <CarLoaderFull /> : <DefaultLoader />}
    </div>
  );
};

export default GlobalTransitionLoader;
